/**
 * Margin computation, title sizing, and the unified layout orchestrator.
 */

import { TITLE_SPACING, canvasScale } from './constants';
import { estimateTextWidth } from './axisSpacing';

// ─── Title font-size computation ────────────────────────────────────────────

const TITLE_BASE_FONT_SIZE = 18;
const TITLE_MIN_FONT_SIZE = 12;
const TITLE_CHAR_WIDTH_RATIO = 0.55;

export function computeTitleFontSize(title, chartWidth, estimatedMarginLeft = 80) {
  if (!title) return 0;
  const available = chartWidth - estimatedMarginLeft - 20;
  const textWidth = title.length * TITLE_BASE_FONT_SIZE * TITLE_CHAR_WIDTH_RATIO;
  if (textWidth <= available) return TITLE_BASE_FONT_SIZE;
  return Math.max(TITLE_MIN_FONT_SIZE, Math.floor(TITLE_BASE_FONT_SIZE * (available / textWidth)));
}

/** Style object for the title div. */
export function computeTitleStyle(chartWidth, marginLeft, marginRight = 20) {
  return {
    maxWidth: chartWidth - marginLeft - marginRight,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

/** Dynamic top margin based on title presence. */
export function computeTopMargin(title, titleFontSize, hScale = 1) {
  if (!title) return Math.round(TITLE_SPACING.noTitleTopPad * hScale);
  const gap = Math.round(TITLE_SPACING.titleToContentGap * hScale);
  if (titleFontSize != null) return titleFontSize + gap;
  const base = title.length > 50 ? 56 : 44;
  return Math.round(base * hScale);
}

/** Compute bottom margin from measured label extent. */
export function computeBottomMarginFromExtent(labelExtent, { hasXLabel = false, rotation = 0 } = {}) {
  const tickMarkSpace = 8;
  const axisLineSpace = 4;
  const labelTitleGap = hasXLabel ? 28 : 0;
  if (!rotation) {
    return axisLineSpace + tickMarkSpace + Math.ceil(labelExtent) + labelTitleGap;
  }
  return axisLineSpace + Math.ceil(labelExtent) + tickMarkSpace + labelTitleGap;
}

/** Dynamic left margin based on widest Y tick value. */
export function computeLeftMargin(yTickValues, fontSize = 13, hasYLabel = false, fontFamily = 'sans-serif') {
  if (!yTickValues || yTickValues.length === 0) {
    return hasYLabel ? 70 : 50;
  }
  const maxTickWidth = Math.max(
    ...yTickValues.map(v => estimateTextWidth(String(v), fontSize, fontFamily))
  );
  const tickPadding = 14;
  const yLabelSpace = hasYLabel ? 34 : 0;
  return Math.max(50, Math.ceil(maxTickWidth + tickPadding + yLabelSpace));
}

/** Dynamic bottom margin accounting for rotated labels. */
export function computeBottomMargin(xLabelStrategy, hasXLabel = false) {
  const base = 34;
  const labelHeight = hasXLabel ? 28 : 0;

  if (xLabelStrategy.rotation === 0) {
    return base + 20 + labelHeight;
  }

  const rotatedHeight =
    xLabelStrategy.maxChars *
    xLabelStrategy.fontSize *
    0.6 *
    Math.sin((xLabelStrategy.rotation * Math.PI) / 180);
  const cappedHeight = Math.min(rotatedHeight, 100);
  return base + Math.ceil(cappedHeight) + 8 + labelHeight;
}

/**
 * Determine how to render x-axis category labels.
 * Returns { rotation, maxChars, fontSize, tickInterval }.
 */
export function computeXLabelStrategy(labels, availableWidth, fontSize = 13, fontFamily = 'sans-serif') {
  const count = labels.length;
  if (count === 0) return { rotation: 0, maxChars: 20, fontSize, tickInterval: 1 };

  const slotWidth = availableWidth / count;
  const longestLabelText = labels.reduce((maxLabel, label) => (
    String(label).length > String(maxLabel).length ? label : maxLabel
  ), '');
  const longestLabel = String(longestLabelText).length;
  const longestPx = estimateTextWidth(longestLabelText, fontSize, fontFamily);

  if (longestPx <= slotWidth - 4) {
    return { rotation: 0, maxChars: longestLabel, fontSize, tickInterval: 1 };
  }

  const rotatedFit = Math.floor((slotWidth * 1.4) / (fontSize * 0.6));
  if (rotatedFit >= 5) {
    return { rotation: 45, maxChars: Math.min(rotatedFit, 20), fontSize, tickInterval: 1 };
  }

  const MIN_FONT = 9;
  const SPACING_FACTOR = 0.7;
  const MAX_LABEL_HEIGHT_PX = 90;
  const font45dense = Math.min(fontSize, Math.max(MIN_FONT, Math.floor(slotWidth / SPACING_FACTOR)));
  if (font45dense >= MIN_FONT) {
    const maxChars45 = Math.min(Math.floor(MAX_LABEL_HEIGHT_PX / (font45dense * 0.6)), longestLabel);
    return { rotation: 45, maxChars: Math.max(4, maxChars45), fontSize: font45dense, tickInterval: 1 };
  }

  const neededSlot = MIN_FONT * SPACING_FACTOR;
  const tickInterval = Math.max(1, Math.ceil(neededSlot / slotWidth));
  const maxChars45 = Math.min(Math.floor(MAX_LABEL_HEIGHT_PX / (MIN_FONT * 0.6)), longestLabel);
  return { rotation: 45, maxChars: Math.max(4, maxChars45), fontSize: MIN_FONT, tickInterval };
}

/** Compute left margin for horizontal bar charts. */
export function computeHorizontalLeftMargin(labels, fontSize = 13, maxMargin = 180, fontFamily = 'sans-serif') {
  if (!labels || labels.length === 0) return 65;
  const longestPx = Math.max(...labels.map(l => estimateTextWidth(String(l), fontSize, fontFamily)));
  return Math.max(65, Math.min(maxMargin, Math.ceil(longestPx + 18)));
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function normalizeMargins(margin) {
  return {
    top: Math.max(0, Math.round(margin.top || 0)),
    right: Math.max(0, Math.round(margin.right || 0)),
    bottom: Math.max(0, Math.round(margin.bottom || 0)),
    left: Math.max(0, Math.round(margin.left || 0)),
  };
}

/**
 * Unified chart layout orchestrator.
 */
export function computeChartLayout({
  width,
  height,
  title = '',
  titleFontSize,
  xLabels = [],
  yTickValues = [],
  tickFontSize = 13,
  tickFontFamily = 'sans-serif',
  hasXLabel = false,
  hasYLabel = false,
  orientation = 'vertical',
  baseRight = 80,
  baseBottom = 70,
  baseTop = 20,
  legend = { show: false, position: 'top', topSpace: 22, bottomSpace: 80, rightSpace: 150 },
  valuesRightSpace = 0,
  xLabelStrategy: xLabelStrategyOverride,
  xAxisResolved,
  extra = {},
  horizontalLeftMax = 180,
  minInnerWidth = 120,
  minInnerHeight = 120,
}) {
  const { w: wScale, h: hScale } = canvasScale(width, height);

  const scaledBaseRight = Math.round(baseRight * wScale);
  const scaledBaseBottom = Math.round(baseBottom * hScale);
  const scaledBaseTop = Math.round(baseTop * hScale);
  const scaledHorizontalLeftMax = Math.round(horizontalLeftMax * wScale);

  const scaledLegendTopSpace = Math.round((legend.topSpace ?? 22) * hScale);
  const scaledLegendBottomSpace = Math.round((legend.bottomSpace ?? 80) * hScale);
  const scaledLegendRightSpace = Math.round((legend.rightSpace ?? 150) * wScale);

  const topBase = title ? computeTopMargin(title, titleFontSize, hScale) : scaledBaseTop;
  const topLegend = legend.show && legend.position === 'top' ? scaledLegendTopSpace : 0;
  const rightLegend = legend.show && legend.position === 'right' ? scaledLegendRightSpace : 0;
  const bottomLegend = legend.show && legend.position === 'bottom' ? scaledLegendBottomSpace : 0;

  const approxInnerWidth = Math.max(120, width - 140 * wScale);
  const xLabelStrategy = xLabelStrategyOverride || computeXLabelStrategy(
    xLabels,
    approxInnerWidth,
    tickFontSize,
    tickFontFamily,
  );

  const left = orientation === 'horizontal'
    ? computeHorizontalLeftMargin(xLabels, tickFontSize, scaledHorizontalLeftMax, tickFontFamily) + (hasYLabel ? Math.round(34 * wScale) : 0)
    : computeLeftMargin(yTickValues, tickFontSize, hasYLabel, tickFontFamily);

  const bottom = orientation === 'horizontal'
    ? scaledBaseBottom
    : xAxisResolved
      ? computeBottomMarginFromExtent(xAxisResolved.labelExtent, { hasXLabel, rotation: xAxisResolved.rotation })
      : computeBottomMargin(xLabelStrategy, hasXLabel);

  const margin = normalizeMargins({
    top: topBase + topLegend + (extra.top || 0),
    right: scaledBaseRight + rightLegend + valuesRightSpace + (extra.right || 0),
    bottom: Math.max(bottom, bottomLegend) + (extra.bottom || 0),
    left: left + (extra.left || 0),
  });

  let innerWidth = Math.round(width - margin.left - margin.right);
  let innerHeight = Math.round(height - margin.top - margin.bottom);

  if (innerWidth < minInnerWidth) {
    const missing = minInnerWidth - innerWidth;
    const reduceLeft = Math.min(margin.left - 30, Math.ceil(missing / 2));
    const reduceRight = Math.min(margin.right - 20, missing - reduceLeft);
    margin.left = clamp(margin.left - reduceLeft, 30, margin.left);
    margin.right = clamp(margin.right - reduceRight, 20, margin.right);
    innerWidth = Math.round(width - margin.left - margin.right);
  }

  if (innerHeight < minInnerHeight) {
    const missing = minInnerHeight - innerHeight;
    const reduceTop = Math.min(margin.top - 16, Math.ceil(missing / 2));
    const reduceBottom = Math.min(margin.bottom - 20, missing - reduceTop);
    margin.top = clamp(margin.top - reduceTop, 16, margin.top);
    margin.bottom = clamp(margin.bottom - reduceBottom, 20, margin.bottom);
    innerHeight = Math.round(height - margin.top - margin.bottom);
  }

  return {
    margin,
    innerWidth: Math.max(0, innerWidth),
    innerHeight: Math.max(0, innerHeight),
    xLabelStrategy,
  };
}

/**
 * Proportional bar width.
 */
export function computeBarWidth(bandwidth, chartDimension, categoryCount, opts = {}) {
  const { maxFraction = 0.12, minPx = 2 } = opts;
  const proportionalCap = chartDimension * Math.min(maxFraction, 0.7 / categoryCount);
  return Math.max(minPx, Math.min(bandwidth, proportionalCap));
}
